import { RefObject, useEffect, useRef, useState } from 'react';
import { gtm, selectElement } from 'tracking';

const TRACKING_NAME = 'PDP Reviews';
/**
 * Helper hook to add an event listener to a widget element
 */
export const useWidgetEvent = (
  widget: HTMLDivElement | null,
  selector: string,
  eventType: string,
  eventHandler: (element: Element, event: Event) => void
): void => {
  useEffect(() => {
    if (widget) {
      const handler = (event: Event): void => {
        const target = event.target as Element;
        if (target.closest(selector)) {
          eventHandler(target, event);
        }
      };

      widget.addEventListener(eventType, handler, true);

      return () => {
        widget.removeEventListener(eventType, handler, true);
      };
    }
  }, [widget, selector, eventType, eventHandler]);
};

/**
 * Hook that tracks user interaction with the JudgeMe Reviews widget
 */
const useJudgeMeReviewsTracking = (
  widgetRef: RefObject<HTMLDivElement>
): void => {
  const [widget, setWidget] = useState<HTMLDivElement | null>(null);
  const handledEventsRef = useRef(new Set<Event>());

  // Since the widget is loaded asynchronously from the JudgeMe script injected on top level,
  // we need to wait for it to be available before we can add event listeners to it.
  // The timeout is cleared when the component unmounts to prevent memory leaks.
  useEffect(() => {
    if (widgetRef.current !== null) {
      setWidget(widgetRef.current);
    } else {
      const timeoutId = setTimeout(() => {
        setWidget(widgetRef.current);
      }, 1000);

      return () => clearTimeout(timeoutId);
    }
  }, [widgetRef]);

  // Track use of the load more button
  useWidgetEvent(widget, '.jdgm-paginate__load-more', 'click', () =>
    gtm(selectElement({ element: [TRACKING_NAME, 'Load More Reviews'] }))
  );

  // Track use of the buttons to filter by rating
  useEffect(() => {
    if (widget) {
      const filterRaringButtons = widget.querySelectorAll(
        '.jdgm-histogram__row'
      );
      const handlers: ((event: Event) => void)[] = [];

      filterRaringButtons.forEach((button) => {
        const clickHandler = (event: Event): void => {
          handledEventsRef.current.add(event);

          const dataRating = button.getAttribute('data-rating');
          if (!dataRating) return;
          gtm(
            selectElement({
              element: [
                TRACKING_NAME,
                'Filter by Rating',
                dataRating === 'null' ? 'Reset' : dataRating,
              ],
            })
          );
        };

        handlers.push(clickHandler);
        button.addEventListener('click', clickHandler);
      });

      return () => {
        filterRaringButtons.forEach((button, index) => {
          button.removeEventListener('click', handlers[index]);
        });
      };
    }
  }, [widget]);

  // Track use of the dropdown to sort reviews
  useWidgetEvent(
    widget,
    '.jdgm-sort-dropdown',
    'change',
    (sortingSelectButton) => {
      const selectedOption = (sortingSelectButton as HTMLSelectElement).value;
      gtm(
        selectElement({
          element: [TRACKING_NAME, 'Sort Reviews', selectedOption],
        })
      );
    }
  );

  // Track generic clicks on the widget
  useEffect(() => {
    if (widget) {
      const clickHandler = (event: Event): void => {
        if (handledEventsRef.current.has(event)) {
          handledEventsRef.current.delete(event);
          return;
        }

        const target = event.target as HTMLElement;

        if (
          target.matches('.jdgm-paginate__load-more') ||
          target.matches('.jdgm-histogram__row') ||
          target.matches('.jdgm-sort-dropdown')
        ) {
          return;
        }

        gtm(
          selectElement({
            element: [TRACKING_NAME, 'Generic Click'],
          })
        );
      };

      widget.addEventListener('click', clickHandler);

      return () => {
        widget.removeEventListener('click', clickHandler);
      };
    }
  }, [widget]);
};

export default useJudgeMeReviewsTracking;
